/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import {
	shippingTitleBookSX,
	shippingTitleGeneralSX,
} from '@/components/content/CheckOut/parts/Shipping/custom/styles/typography';
import { useLocalization } from '@/data/Localization';
import { EditableAddress } from '@/data/types/Address';

import { REG_EX } from '@/utils/address';

import {
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { ChangeEvent, FC, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import {
	AccountInformationBpCheckboxSX,
	AccountInformationFormCheckboxSX,
} from '@/components/content/Account/styles/accountInformationForm';
import ErrorIcon from '@mui/icons-material/Error';
import { formatPhoneNumber, formatPhoneNumberValue } from '@/utils/InputPhone';
import { BpCheckbox } from '@/components/content/Account/parts/InformationForm';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCart } from '@/data/Content/Cart';
import { useExtendedZoneValidate } from '@/data/Content/ExtendedZone';

import { EMPTY_STRING } from '@/data/constants/marketing';
import { useZipCode } from '@/data/hooks/useZipCode';
import { ExtendedZoneAlert } from '@/components/blocks/ExtendedZoneAlert';
import { cleanTextFomEmojis } from '@/utils/cleanTextFomEmojis';
import { useAddressFormValidation } from '@/components/blocks/AddressForm/hooks/useAddressFormFieldsForm';

type Props = {
	nickNameDisabled: boolean;
	address: EditableAddress;
	handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
	selectChange: (event: SelectChangeEvent) => void;
	editPerson?: boolean;
	setSubmitting?: any;
	addressBook?: boolean;
};

export const AddressFormFields: FC<Props> = ({
	address,
	handleChange: _handleChange,
	selectChange,
	nickNameDisabled = false,
	editPerson,
	setSubmitting,
	addressBook,
}) => {
	const addressFormNLS = useLocalization('AddressForm');
	const { data: cart } = useCart();
	const { validateExtendedZone } = useExtendedZoneValidate();
	const CustomIconComponent = (props: any) => (
		<KeyboardArrowDownIcon
			{...props}
			sx={{ color: address.zipCode.length === 5 ? '#0467D2' : '#b5beca' }}
		/>
	);
	const [isExtendedZoneShipping, setIsExtendedZoneShipping] = useState(false);
	const [customNeighborhood, setCustomNeighborhood] = useState<boolean>(false);
	const [openSelect, setOpenSelect] = useState<boolean>(false);
	const [referencia, setReferencia] = useState<any>(address.rfc);
	const initialExecuteRef = useRef<boolean>(true);
	const { data: solemtiAddress, isLoading: isValidatingZipCode } = useZipCode(address.zipCode);
	const { errors, onReadyValidateAllFields, onValidateField, onCleanErrorField, onSetFieldError } =
		useAddressFormValidation();

	const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.name;
		const value = event.target.value;
		onValidateField(name, value);
		_handleChange(event);
	}, [_handleChange, onValidateField]);

	const addressInfo = useMemo(() => {
		const solemtiAddressData = solemtiAddress?.[0];
		if (!solemtiAddressData) {
			return {
				city: '',
				state: '',
				neighborhood: [],
			};
		}

		const city = solemtiAddressData.Municipio ?? solemtiAddressData.Ciudad ?? 'Unknown';
		const state = solemtiAddressData.Estado;
		const neighborhood = solemtiAddress?.map((item) => item.Asentamiento);

		return {
			city,
			state,
			neighborhood,
		};
	}, [solemtiAddress]);
	const lastSyncedAddressInfo = useRef<{ city: string; state: string; neighborhoodLen: number } | null>(null);

	useEffect(() => {
		if (isValidatingZipCode) {
			return;
		}

		const currentSync = {
			city: addressInfo.city,
			state: addressInfo.state,
			neighborhoodLen: addressInfo.neighborhood.length,
		};

		const prev = lastSyncedAddressInfo.current;
		const infoChanged =
			!prev ||
			prev.city !== currentSync.city ||
			prev.state !== currentSync.state ||
			prev.neighborhoodLen !== currentSync.neighborhoodLen;

		let didChange = false;

		if (address.country !== 'Mexico') {
		handleChange({
				target: { name: 'country', value: 'Mexico' },
		} as ChangeEvent<HTMLInputElement>);
			didChange = true;
		}

		if (address.state !== addressInfo.state) {
		handleChange({
				target: { name: 'state', value: addressInfo.state },
		} as ChangeEvent<HTMLInputElement>);
			didChange = true;
		}

		if (address.city !== addressInfo.city) {
		handleChange({
				target: { name: 'city', value: addressInfo.city },
		} as ChangeEvent<HTMLInputElement>);
			didChange = true;
		}


		if (infoChanged) {
		onValidateField('city', addressInfo.city);
		onValidateField('state', addressInfo.state);

		selectChange({
			target: {
				name: '',
				value: '',
			},
		} as ChangeEvent<HTMLInputElement>);

			lastSyncedAddressInfo.current = currentSync;
		}

		if (addressInfo.neighborhood.length === 0 && address.zipCode.length === 5) {
			onSetFieldError('zipCode', 'No se encontró información para el código postal');
		} else {
			onCleanErrorField('zipCode');
		}
	}, [addressInfo, isValidatingZipCode]);

	useEffect(() => {
		const execute = async () => {
			if (address?.zipCode?.length === 5) {
				const respExtZone = await validateExtendedZone(address.zipCode, cart?.orderId);
				setIsExtendedZoneShipping(!!respExtZone?.shippingWithExtendedZone);
			}
		};

		execute();
	}, [address.zipCode, cart?.orderId]);

	const neighborhoodWithoutAccents = useMemo(
		() =>
			(addressInfo.neighborhood || []).map((nh: string) =>
				nh.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			),
		[addressInfo.neighborhood]
	);

	useEffect(() => {
		if (
			initialExecuteRef.current &&
			address.addressLine2 &&
			neighborhoodWithoutAccents.length > 0 &&
			!neighborhoodWithoutAccents.includes(address.addressLine2)
		) {
			initialExecuteRef.current = false;
			setCustomNeighborhood(true);
		}
	}, [address, neighborhoodWithoutAccents]);

	// Cada que cambia el zipcode
	useEffect(() => {
		if (address.zipCode && address.zipCode.length === 5) {
			setCustomNeighborhood(false);
			setOpenSelect(false);
		} else if (address.zipCode.length < 5) {
			selectChange({
				target: {
					name: 'addressLine2',
					value: '',
				},
			} as ChangeEvent<HTMLInputElement>);
			onCleanErrorField('addressLine2');
		}
	}, [address.zipCode, selectChange]);

	const handleZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const zipCode = event.target.value || '';

		if (/^\d*$/.test(zipCode) && zipCode.length <= 5) {
			handleChange(event);
		}
	};

	const handleNeighborhoodChange = (event: SelectChangeEvent) => {
		if (event.target.value === 'custom') {
			setCustomNeighborhood(true);

			selectChange({
				target: {
					name: 'addressLine2',
					value: '',
				},
			} as ChangeEvent<HTMLInputElement>);
		} else {
			onValidateField(event.target.name, event.target.value);
			selectChange(event);
			onCleanErrorField('addressLine2');
		}
	};

	const handleBackToSelect = () => {
		setCustomNeighborhood(false);
		handleChange({
			target: {
				name: 'addressLine2',
				value: '',
			},
		} as ChangeEvent<HTMLInputElement>);
		setOpenSelect(true);
	};

	const handleReferencia = (event: any) => {
		let valueSinAcentos = event.target.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
		valueSinAcentos = cleanTextFomEmojis(valueSinAcentos);
		setReferencia(valueSinAcentos);
		event.target.value = valueSinAcentos;
		handleChange(event);
	};

	const handleBlurFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
		event
	) => {
		const name = event.target.name;
		const value = event.target.value || (address as any)?.[name];

		onValidateField(name, value);
	};

	useEffect(() => {
		const validateAll = onReadyValidateAllFields(address);
		setSubmitting(!validateAll);
	}, [
		address.firstName,
		address.lastName,
		address.lastNameMat,
		address.phone1,
		address.email1,
		address.nickName,
		address.addressLine1,
		address.addressLine2,
		address.xaddr_numExt,
		address.xaddr_numInt,
		address.zipCode,
		address.city,
		address.state,
		address.country,
		setSubmitting,
	]);

	return (
		<Stack sx={addressFormFieldsSX}>
			{editPerson ? (
				<Grid item container spacing={3} sm={12} paddingTop={'18px'}>
					<Grid item xs={12} sm={6}>
						<TextField
							id="address-form-firstName"
							data-testid="address-form-firstName"
							name="firstName"
							label={addressFormNLS.Labels.FirstName.t()}
							onChange={handleChange}
							error={!!errors.firstName}
							helperText={errors.firstName}
							value={address.firstName}
							inputProps={{ maxLength: 40 }}
							fullWidth
							autoFocus={nickNameDisabled}
							autoComplete="given-name"
							required
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-lastName"
							data-testid="address-form-lastName"
							name="lastName"
							label={'Apellido paterno'}
							onChange={handleChange}
							error={!!errors.lastName}
							helperText={errors.lastName}
							value={address.lastName}
							inputProps={{ maxLength: 40 }}
							fullWidth
							autoComplete="family-name"
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-lastNameMat"
							data-testid="address-form-lastNameMat"
							name="lastNameMat"
							label={'Apellido materno'}
							onChange={handleChange}
							error={!!errors.lastNameMat}
							helperText={errors.lastNameMat}
							value={address.lastNameMat}
							inputProps={{ maxLength: 40 }}
							fullWidth
							autoComplete="family-name"
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							id="address-form-phone1"
							name="phone1"
							type="tel"
							data-testid="address-form-phone1"
							label={'Teléfono'}
							onChange={(event: ChangeEvent<HTMLInputElement>) =>
								formatPhoneNumber(event, handleChange)
							}
							error={!!errors.phone1}
							helperText={errors.phone1}
							value={formatPhoneNumberValue(address.phone1)}
							inputProps={{ maxLength: 32, pattern: REG_EX.PHONE.source }}
							fullWidth
							autoComplete="tel"
							InputProps={{
								endAdornment: (
									<Tooltip
										title={
											<Typography sx={{ fontSize: 'var(--font-size-caption)' }}>
												{'En caso de que tengamos que'} <br />
												{'contactarte sobre tu pedido'}
											</Typography>
										}
										placement="top-end"
										arrow
									>
										<ErrorIcon sx={{ fontSize: 'var(--font-size-body)', color: '#C9C9C9' }} />
									</Tooltip>
								),
							}}
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							required
							id="address-form-email"
							data-testid="address-form-email"
							name="email1"
							type="email"
							label={addressFormNLS.Labels.Email.t()}
							onChange={handleChange}
							value={address.email1}
							error={!!errors.email1}
							helperText={errors.email1}
							inputProps={{ maxLength: 35, pattern: REG_EX.EMAIL.source }}
							fullWidth
							autoComplete="email"
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
						<FormControlLabel
							sx={{ marginLeft: '0px' }}
							control={
								<BpCheckbox
									data-testid={`schedule-recurring-checkbox`}
									id={`schedule-recurring-checkbox`}
									name={`receiveEmail`}
									checked={address.receiveEmail}
									onChange={handleChange}
									sx={AccountInformationBpCheckboxSX}
								/>
							}
							label={
								<Typography sx={AccountInformationFormCheckboxSX}>
									{
										'Deseo recibir promociones y noticias en mi correo electrónico por parte de dportenis.'
									}
								</Typography>
							}
						/>
					</Grid>
				</Grid>
			) : (
				<Grid item container spacing={3} sm={12}>
					<Grid
						paddingLeft={'var(--spacing-6)'}
						paddingTop={addressBook ? '' : 'var(--spacing-6)'}
						xs={12}
						display={'flex'}
						flexDirection={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}
					>
						{address.primary ? (
							<Typography sx={addressBook ? shippingTitleBookSX : shippingTitleGeneralSX}>
								{'DIRECCIÓN DE ENVÍO'}
							</Typography>
						) : (
							<Typography sx={addressBook ? shippingTitleBookSX : shippingTitleGeneralSX}>
								{'NUEVA DIRECCIÓN'}
							</Typography>
						)}
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							data-testid="address-form-nickName"
							id="address-form-nickName"
							name="nickName"
							label={'Destinatario (ej. Mi oficina, mi casa...)'}
							onChange={handleChange}
							value={address.nickName}
							inputProps={{
								required: true,
								maxLength: 128,
								pattern: REG_EX.NICKNAME_ALPHA_NUMERIC_SPECIAL_CHAR.source,
							}}
							error={!!errors.nickName}
							helperText={errors.nickName}
							fullWidth
							autoComplete="nickname"
							required
							autoFocus={address.primary ? true : false}
							disabled={nickNameDisabled}
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="address-form-address-line1"
							id="address-form-address-line1"
							name="addressLine1"
							label={'Calle'}
							onChange={handleChange}
							error={!!errors.addressLine1}
							helperText={errors.addressLine1}
							value={address.addressLine1}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="address-line1"
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="address-form-address-number-ext"
							id="address-form-address-number-ext"
							name="xaddr_numExt"
							label={'Número exterior'}
							onChange={handleChange}
							error={!!errors.xaddr_numExt}
							helperText={errors.xaddr_numExt}
							value={address.xaddr_numExt}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="number-ext"
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							data-testid="address-form-address-number-int"
							id="address-form-address-number-int"
							name="xaddr_numInt"
							label={'Número interior'}
							onChange={handleChange}
							value={address.xaddr_numInt}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="number-int"
							sx={addressFormFieldsSX}
							onBlur={handleBlurFocus}
							onFocus={handleBlurFocus}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-zipCode"
							name="zipCode"
							data-testid="address-form-zipCode"
							label={addressFormNLS.Labels.ZipCode.t()}
							onChange={handleZipCodeChange}
							error={!!errors.zipCode}
							helperText={errors.zipCode}
							value={address.zipCode}
							inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }}
							fullWidth
							autoComplete="postal-code"
							sx={addressFormFieldsSX}
							disabled={isValidatingZipCode}
						/>
						{isValidatingZipCode ? (
							<Grid
								style={{
									display: 'flex',
									alignItems: 'center',
									columnGap: '5px',
									marginTop: '3px',
								}}
							>
								<CircularProgress size={14} thickness={6} style={{ color: '#b5beca' }} />
								<Typography style={{ color: '#b5beca', fontSize: 'var(--font-size-caption)' }}>
									{'Validando código postal...'}
								</Typography>
							</Grid>
						) : isExtendedZoneShipping ? (
							<ExtendedZoneAlert
								sx={{
									marginTop: '5px',
								}}
							/>
						) : null}
					</Grid>
					<Grid item xs={12} sm={6}>
						{isValidatingZipCode ? (
							<Skeleton
								variant="rounded"
								width="100%"
								height="55px"
								sx={{ minHeight: '55px', height: '55px' }}
							/>
						) : (
							<FormControl fullWidth required variant="outlined">
								<InputLabel
									sx={{
										backgroundColor: '#FFF',
										padding: '0 10px 0 5px',
										color: !!errors.addressLine2
											? '#d32f2f'
											: address.zipCode.length !== 5
											? '#b5beca'
											: '',
									}}
									id="address-form-address-line2-label"
								>
									{customNeighborhood ? '' : 'Colonia'}
								</InputLabel>

								{customNeighborhood ? (
									<TextField
										required
										data-testid="address-form-address-line2"
										id="address-form-address-line2"
										name="addressLine2"
										label={'Escribe tu colonia'}
										onChange={handleChange}
										autoFocus={address.addressLine2 === EMPTY_STRING}
										error={!!errors.addressLine2}
										helperText={errors.addressLine2}
										value={address.addressLine2}
										inputProps={{ maxLength: 99 }}
										fullWidth
										autoComplete="address-line2"
										disabled={!!errors['city']}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="volver a seleccionar"
														onClick={handleBackToSelect}
														edge="end"
													>
														<CustomIconComponent />
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={addressFormFieldsSX}
										onBlur={handleBlurFocus}
										onFocus={handleBlurFocus}
									/>
								) : null}
								{!customNeighborhood ? (
									<>
										<Select
											labelId="address-form-address-line2-label"
											id="address-form-address-line2"
											name="addressLine2"
											value={address.addressLine2}
											onChange={handleNeighborhoodChange}
											error={!!errors.addressLine2}
											open={openSelect}
											onOpen={() => setOpenSelect(true)}
											onClose={() => setOpenSelect(false)}
											defaultOpen={false}
											data-testid="address-form-address-line2"
											disabled={address.zipCode.length !== 5 || !!errors['city']}
											IconComponent={CustomIconComponent}
										>
											{neighborhoodWithoutAccents
												? neighborhoodWithoutAccents.map((nh) => (
														<MenuItem key={nh} value={nh}>
															{nh}
														</MenuItem>
												  ))
												: null}
											{address.zipCode ? (
												<MenuItem value="custom">{'Mi colonia no aparece'}</MenuItem>
											) : null}
										</Select>
										{!!errors.addressLine2 ? (
											<FormHelperText sx={{ color: '#d32f2f' }}>
												{errors.addressLine2}
											</FormHelperText>
										) : null}
									</>
								) : null}
							</FormControl>
						)}
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="state-textfield"
							id="state-textfield"
							name="state"
							label={'Estado o provincia'}
							autoComplete="address-level1"
							onChange={handleChange}
							value={address.state}
							inputProps={{ maxLength: 40 }}
							fullWidth
							disabled
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-city"
							data-testid="address-form-city"
							name="city"
							label={'Ciudad o municipio'}
							onChange={handleChange}
							value={address.city}
							inputProps={{ maxLength: 40 }}
							fullWidth
							autoComplete="address-level2"
							disabled
						/>
					</Grid>
					<Grid item xs={12} sm={12}>
						<TextField
							id="address-form-reference"
							data-testid="address-form-reference"
							name="rfc"
							label={'Referencia'}
							onChange={handleReferencia}
							value={referencia}
							inputProps={{ maxLength: 60 }}
							fullWidth
						/>
					</Grid>
				</Grid>
			)}
		</Stack>
	);
};
